import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, OWNER_ID, ROOFING_CONTRACTOR_ID, SITE_URL } from "@/components/site/site-config";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture, OWNER_SALES_TEAM, OWNER_DUMP_TRAILER } from "@/components/site/images";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Destination Roofing Inc. | Citrus County, FL" },
      { name: "description", content: "Meet Deston Judy and learn about Destination Roofing, a local family-owned, licensed and insured roofing company serving Citrus County, FL." },
      { property: "og:title", content: "About Destination Roofing Inc." },
      { property: "og:description", content: "Family-owned, licensed and insured roofing contractor in Citrus County, FL." },
      { property: "og:url", content: canonical("/about") },
      { property: "og:type", content: "profile" },
      { property: "og:image", content: OWNER_SALES_TEAM.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/about") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "About", href: "/about" }]),
        {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          url: canonical("/about"),
          name: "About Destination Roofing Inc.",
          mainEntity: { "@id": ROOFING_CONTRACTOR_ID },
        },
        {
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": OWNER_ID,
          name: "Deston Judy",
          jobTitle: "Owner",
          worksFor: { "@id": ROOFING_CONTRACTOR_ID },
          url: SITE_URL + "/about#deston-judy",
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "About", href: "/about" }]} />
      <PageHero
        eyebrow="About Us"
        title="A Local, Family-Owned Citrus County Roofing Company"
        subtitle="Deston and Kate Judy — Florida natives, longtime Citrus County residents, and the people behind Destination Roofing Inc."
      />
      <section className="mx-auto max-w-6xl px-4 py-14 md:py-20 grid gap-10 lg:grid-cols-3">
        <article className="lg:col-span-2 prose prose-slate max-w-none" id="deston-judy">
          <h2>Who we are</h2>
          <p>
            Destination Roofing Inc. is a family-owned Florida roofing contractor built around one idea: give homeowners a real, honest answer about their roof and do quality work at a fair price. Deston Judy runs the company alongside his wife Kate. We are Florida natives and longtime Citrus County residents — this is our community, and our reputation lives here.
          </p>

          <h2>Licensed and insured</h2>
          <p>
            Destination Roofing is licensed and insured in the State of Florida ({BUSINESS.license}). Every roof we install meets or exceeds current Florida roofing requirements. We are also an <strong>{BUSINESS.atlasCredential}</strong>, which reflects specific training on Atlas shingle products — a manufacturer whose Scotchgard-protected shingles we frequently recommend for Florida homes.
          </p>

          <h2>What you can expect from us</h2>
          <ul>
            <li>An honest, no-pressure inspection with a written estimate</li>
            <li>A plain-English explanation of what your roof actually needs</li>
            <li>Quality installation to Florida wind standards</li>
            <li>Clean-up, magnetic sweep of nails, and clear communication throughout the job</li>
            <li>Manufacturer warranty plus our workmanship warranty on new roof installations</li>
          </ul>

          <h2>Careful about what we promise</h2>
          <p>
            We do not promise things we can't guarantee. Wind-mitigation credits, insurance coverage on storm-damage claims, and warranty outcomes all depend on your specific home, inspection results and insurance company. We'll tell you what may qualify — and what won't — so you can make a decision without surprises.
          </p>

          <h2>How to reach us</h2>
          <p>
            Call <a href={BUSINESS.phoneHref}>{BUSINESS.phone}</a> or email{" "}
            <a href={BUSINESS.emailHref}>{BUSINESS.email}</a>. You can also{" "}
            <Link to="/contact">request a Free Roof Inspection & Estimate</Link>,{" "}
            <Link to="/reviews">read reviews from Citrus County homeowners</Link>, or{" "}
            <Link to="/gallery">see recent roofing projects</Link>.
          </p>
        </article>
        <aside className="space-y-6">
          <figure className="rounded-xl overflow-hidden border border-border bg-white shadow-[var(--shadow-card)]">
            <ResponsivePicture
              image={OWNER_SALES_TEAM}
              alt="Destination Roofing owner and sales team in Crystal River, Florida"
              sizes="(min-width: 1024px) 33vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">The Destination Roofing team serving Crystal River and Citrus County.</figcaption>
          </figure>
          <figure className="rounded-xl overflow-hidden border border-border bg-white shadow-[var(--shadow-card)]">
            <ResponsivePicture
              image={OWNER_DUMP_TRAILER}
              alt="Destination Roofing owner Deston Judy beside the company dump trailer in Crystal River, Florida"
              sizes="(min-width: 1024px) 33vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">Owner Deston Judy and Destination Roofing's branded dump trailer.</figcaption>
          </figure>
        </aside>
      </section>
      <CTASection />
    </>
  );
}
